import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminMarketing.css';
import { fmtDateTime } from '../../../utils/ruDates';

const STATUS_LABELS = {
    running: 'идёт',
    success: 'статья создана',
    empty: 'нечего публиковать',
    error: 'ошибка',
};

function splitList(value, separator) {
    return String(value || '')
        .split(separator)
        .map((item) => item.trim())
        .filter(Boolean);
}

function splitLines(value) {
    return splitList(value, '\n');
}

// Показываем расписание словами: «10:00, 11:00 и 12:00» понятнее чисел в полях
function formatQueue(delayMinutes, intervalMinutes) {
    const delay = Number(delayMinutes ?? 60);
    const interval = Number(intervalMinutes ?? 60);
    if (interval <= 0 && delay <= 0) return 'тот же момент, все сразу';

    const times = [0, 1, 2].map((index) => {
        const minutes = 9 * 60 + delay + interval * index;
        const hh = String(Math.floor(minutes / 60) % 24).padStart(2, '0');
        const mm = String(minutes % 60).padStart(2, '0');
        return `${hh}:${mm}`;
    });
    return `${times[0]}, затем ${times[1]} и ${times[2]}`;
}

function fmtDuration(seconds) {
    if (!seconds && seconds !== 0) return '—';
    if (seconds < 60) return `${seconds} с`;
    return `${Math.floor(seconds / 60)} м ${seconds % 60} с`;
}

function AdminContentAgent() {
    const { request } = useApi();
    const [config, setConfig] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [openRunId, setOpenRunId] = useState(null);
    // Списки редактируются как обычный текст: разбор на элементы при сохранении.
    // Иначе перевод строки и запятая исчезают прямо под пальцами.
    const [domainsText, setDomainsText] = useState('');
    const [tagsText, setTagsText] = useState('');
    // Ручной прогон может повторить слот расписания либо идти на общей задаче
    const [manualTopic, setManualTopic] = useState('');
    // Канал и задача для заметки; токен бота живёт в окружении
    const [telegram, setTelegram] = useState(null);
    const pollRef = useRef(null);

    const loadRuns = useCallback(async () => {
        try {
            const data = await request('/api/v1/admin/content-agent/runs?limit=30');
            setRuns(data?.items || []);
            return data?.items || [];
        } catch (error) {
            logger.error('Не удалось загрузить историю прогонов', error);
            return [];
        }
    }, [request]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await request('/api/v1/admin/content-agent/config');
            setConfig(data);
            try {
                setTelegram(await request('/api/v1/admin/blog/telegram'));
            } catch (error) {
                logger.warn('Настройки Телеграма недоступны', error);
                setTelegram({ unavailable: true, error: error?.message || 'нет доступа' });
            }
            setDomainsText((data?.allowed_domains || []).join('\n'));
            setTagsText((data?.tags || []).join(', '));
            await loadRuns();
        } catch (error) {
            logger.error('Не удалось загрузить настройки агента', error);
            setMessage({ type: 'error', text: 'Не удалось загрузить настройки' });
        } finally {
            setLoading(false);
        }
    }, [request, loadRuns]);

    useEffect(() => {
        load();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [load]);

    const setField = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

    const updateTopic = (index, field, value) =>
        setConfig((prev) => ({
            ...prev,
            topics: (prev.topics || []).map((topic, i) =>
                i === index ? { ...topic, [field]: value } : topic
            ),
        }));

    const addTopic = () =>
        setConfig((prev) => ({
            ...prev,
            topics: [
                ...(prev.topics || []),
                { name: 'Новая тема', task: '', times: ['12:00'], articles: 1, enabled: true },
            ],
        }));

    const removeTopic = (index) =>
        setConfig((prev) => ({
            ...prev,
            topics: (prev.topics || []).filter((_, i) => i !== index),
        }));

    const updateTime = (topicIndex, timeIndex, value) =>
        setConfig((prev) => ({
            ...prev,
            topics: (prev.topics || []).map((topic, i) =>
                i === topicIndex
                    ? { ...topic, times: (topic.times || []).map((t, j) => (j === timeIndex ? value : t)) }
                    : topic
            ),
        }));

    const addTime = (topicIndex) =>
        setConfig((prev) => ({
            ...prev,
            topics: (prev.topics || []).map((topic, i) =>
                i === topicIndex ? { ...topic, times: [...(topic.times || []), '12:00'] } : topic
            ),
        }));

    const removeTime = (topicIndex, timeIndex) =>
        setConfig((prev) => ({
            ...prev,
            topics: (prev.topics || []).map((topic, i) =>
                i === topicIndex
                    ? { ...topic, times: (topic.times || []).filter((_, j) => j !== timeIndex) }
                    : topic
            ),
        }));

    const save = async () => {
        setLoading(true);
        setMessage(null);
        try {
            const payload = {
                enabled: config.enabled,
                base_url: config.base_url || '',
                model: config.model || 'default',
                topics: (config.topics || []).filter((topic) => topic.name?.trim() && (topic.times || []).length),
                task_prompt: config.task_prompt || '',
                allowed_domains: splitLines(domainsText),
                articles_per_run: config.articles_per_run || 1,
                category: config.category || '',
                tags: splitList(tagsText, ','),
                auto_publish: config.auto_publish,
                publish_first_delay_minutes: config.publish_first_delay_minutes ?? 60,
                publish_interval_minutes: config.publish_interval_minutes ?? 60,
                telegram_autopost: config.telegram_autopost,
                image_enabled: config.image_enabled,
                image_prompt: config.image_prompt || '',
                cta_enabled: config.cta_enabled,
                cta_promo_amount: config.cta_promo_amount ?? 5000,
                cta_promo_valid_days: config.cta_promo_valid_days ?? 30,
                cta_promo_max_uses: config.cta_promo_max_uses ?? 25,
                cta_max_per_week: config.cta_max_per_week ?? 2,
                default_promo_code: config.default_promo_code || '',
                default_promo_note: config.default_promo_note || '',
            };
            // Ключи отправляем только когда их реально ввели — иначе затрём сохранённые
            if (config.api_key) payload.api_key = config.api_key;

            const saved = await request('/api/v1/admin/content-agent/config', {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            setConfig(saved);
            setDomainsText((saved?.allowed_domains || []).join('\n'));
            setTagsText((saved?.tags || []).join(', '));

            // Канал сохраняем здесь же: отдельная кнопка приводила к тому,
            // что настройки Телеграма молча оставались пустыми
            if (telegram && !telegram.unavailable) {
                const savedTelegram = await request('/api/v1/admin/blog/telegram', {
                    method: 'PUT',
                    body: JSON.stringify({
                        enabled: Boolean(telegram.enabled),
                        channel: telegram.channel || '',
                        note_task: telegram.note_task || '',
                    }),
                });
                setTelegram(savedTelegram);
            }

            setMessage({ type: 'ok', text: 'Сохранено' });
        } catch (error) {
            logger.error('Не удалось сохранить настройки агента', error);
            setMessage({ type: 'error', text: error?.message || 'Не удалось сохранить' });
        } finally {
            setLoading(false);
        }
    };

    const runNow = async () => {
        setMessage(null);
        try {
            const query = manualTopic ? `?topic=${encodeURIComponent(manualTopic)}` : '';
            await request(`/api/v1/admin/content-agent/run${query}`, { method: 'POST' });
            setMessage({ type: 'ok', text: 'Прогон запущен — поиск и написание занимают несколько минут' });
            await loadRuns();

            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                const items = await loadRuns();
                if (!items.some((item) => item.status === 'running')) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                }
            }, 15000);
        } catch (error) {
            logger.error('Не удалось запустить прогон', error);
            setMessage({ type: 'error', text: error?.message || 'Не удалось запустить прогон' });
        }
    };

    if (!config) {
        return (
            <div className="mkt-page">
                <h1>Генерация статей</h1>
                <div className="mkt-empty">{loading ? 'Загрузка…' : 'Настройки недоступны'}</div>
            </div>
        );
    }

    return (
        <div className="mkt-page">
            <h1>Генерация статей</h1>
            <p className="mkt-page__hint">
                Агент сам ищет тему на разрешённых сайтах и пишет статью — навык поиска и системный промт
                настраиваются на его стороне. Здесь задаётся то, чего агент про нас не знает: список сайтов,
                рубрика блога, расписание и правила публикации.
            </p>

            <div className="mkt-card">
                <div className="mkt-card__title">Подключение</div>
                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>Базовый URL агента</span>
                        <input
                            value={config.base_url || ''}
                            onChange={(e) => setField('base_url', e.target.value)}
                            placeholder="https://agent.timeweb.cloud/v1"
                        />
                    </label>
                    <label className="mkt-field">
                        <span>
                            Ключ{' '}
                            {config.api_key_set
                                ? `(сохранён: ${config.api_key_hint}, введите новый чтобы заменить)`
                                : '(не задан)'}
                        </span>
                        <input
                            type="password"
                            value={config.api_key || ''}
                            onChange={(e) => setField('api_key', e.target.value)}
                            placeholder="••••••••"
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Модель</span>
                        <input
                            value={config.model || ''}
                            onChange={(e) => setField('model', e.target.value)}
                            placeholder="default"
                        />
                    </label>
                </div>
                <div className="mkt-actions">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={Boolean(config.enabled)}
                            onChange={(e) => setField('enabled', e.target.checked)}
                        />
                        Генерация включена
                    </label>
                </div>
                {config.same_agent_as_chat && (
                    <div className="mkt-message mkt-message--error">
                        Указан тот же агент, что обслуживает AI-чат: идентификатор в URL совпадает.
                        Статьи будет писать чат-агент со своим промтом. Ключ у агентов может быть
                        общим, а URL должен отличаться.
                    </div>
                )}
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Темы и публикация</div>
                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>Категория блога</span>
                        <input
                            value={config.category || ''}
                            onChange={(e) => setField('category', e.target.value)}
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Теги, которые добавлять каждой статье</span>
                        <input
                            value={tagsText}
                            onChange={(e) => setTagsText(e.target.value)}
                        />
                    </label>
                </div>
                <div className="mkt-card__title" style={{ marginTop: 18 }}>Темы поиска</div>
                <p className="mkt-page__hint" style={{ marginBottom: 10 }}>
                    Сначала задача: что ищем и о чём писать. Потом частота: сколько раз в сутки
                    и в какое время эту тему запускать. Пустая задача — берётся общая из блока
                    «Сайты и задача».
                </p>
                {(config.topics || []).length === 0 && (
                    <div className="mkt-empty">Тем нет, по расписанию агент запускаться не будет</div>
                )}
                {(config.topics || []).map((topic, index) => (
                    <div
                        key={index}
                        style={{
                            border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: 10,
                            padding: 14,
                            marginBottom: 12,
                            opacity: topic.enabled === false ? 0.55 : 1,
                        }}
                    >
                        <div className="mkt-grid">
                            <label className="mkt-field">
                                <span>Название темы</span>
                                <input
                                    value={topic.name || ''}
                                    onChange={(e) => updateTopic(index, 'name', e.target.value)}
                                    placeholder="Реклама на Ozon"
                                />
                            </label>
                            <label className="mkt-field">
                                <span>Статей за запуск</span>
                                <input
                                    type="number"
                                    min={1}
                                    max={5}
                                    value={topic.articles ?? 1}
                                    onChange={(e) => updateTopic(index, 'articles', Number(e.target.value))}
                                />
                            </label>
                        </div>

                        <label className="mkt-field" style={{ marginTop: 10 }}>
                            <span>Что искать и о чём писать</span>
                            <textarea
                                rows={3}
                                value={topic.task || ''}
                                onChange={(e) => updateTopic(index, 'task', e.target.value)}
                                placeholder="Изменения в рекламных инструментах Ozon: ставки, форматы, правила модерации."
                            />
                        </label>

                        <div className="mkt-actions" style={{ marginTop: 10 }}>
                            <span style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
                                Запускать в:
                            </span>
                            {(topic.times || []).map((time, timeIndex) => (
                                <span key={timeIndex} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input
                                        type="time"
                                        value={time}
                                        onChange={(e) => updateTime(index, timeIndex, e.target.value)}
                                        style={{ padding: '6px 8px' }}
                                    />
                                    <button type="button" onClick={() => removeTime(index, timeIndex)}>×</button>
                                </span>
                            ))}
                            <button type="button" onClick={() => addTime(index)}>Добавить время</button>
                        </div>

                        <div className="mkt-actions">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <input
                                    type="checkbox"
                                    checked={topic.enabled !== false}
                                    onChange={(e) => updateTopic(index, 'enabled', e.target.checked)}
                                />
                                Тема активна
                            </label>
                            <button type="button" onClick={() => removeTopic(index)}>Удалить тему</button>
                        </div>
                    </div>
                ))}
                <div className="mkt-actions">
                    <button type="button" onClick={addTopic}>Добавить тему</button>
                </div>

                <div className="mkt-actions">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={Boolean(config.auto_publish)}
                            onChange={(e) => setField('auto_publish', e.target.checked)}
                        />
                        Публиковать сразу (иначе черновик в CMS)
                    </label>
                </div>
                {config.auto_publish && (
                    <>
                        <div className="mkt-grid" style={{ marginTop: 12 }}>
                            <label className="mkt-field">
                                <span>Первая статья через, мин</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={1440}
                                    value={config.publish_first_delay_minutes ?? 60}
                                    onChange={(e) =>
                                        setField('publish_first_delay_minutes', Number(e.target.value))
                                    }
                                />
                            </label>
                            <label className="mkt-field">
                                <span>Интервал между статьями, мин (0 — все сразу)</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={1440}
                                    value={config.publish_interval_minutes ?? 60}
                                    onChange={(e) =>
                                        setField('publish_interval_minutes', Number(e.target.value))
                                    }
                                />
                            </label>
                        </div>
                        <div className="mkt-actions">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(config.telegram_autopost)}
                                    onChange={(e) => setField('telegram_autopost', e.target.checked)}
                                />
                                Сразу публиковать заметку в Телеграм
                            </label>
                        </div>
                        <div className="mkt-message">
                            Сбор в 09:00, первая статья в{' '}
                            {formatQueue(config.publish_first_delay_minutes, config.publish_interval_minutes)}.
                            Статья с источником вне белого списка всё равно останется черновиком.
                            {config.telegram_autopost
                                ? ' Заметка уходит в канал без вычитки.'
                                : ''}
                        </div>
                    </>
                )}
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Сайты и задача</div>
                <label className="mkt-field">
                    <span>Разрешённые сайты — по одному домену в строке</span>
                    <textarea
                        rows={6}
                        value={domainsText}
                        onChange={(e) => setDomainsText(e.target.value)}
                        placeholder={'new-retail.ru\nrb.ru\nvc.ru'}
                    />
                </label>
                <label className="mkt-field" style={{ marginTop: 12 }}>
                    <span>Задача агенту — необязательно, дополняет его системный промт</span>
                    <textarea
                        rows={5}
                        value={config.task_prompt || ''}
                        onChange={(e) => setField('task_prompt', e.target.value)}
                        placeholder="Найди свежую новость про изменения в логистике Ozon и разбери, что она значит для продавцов."
                    />
                </label>
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Обложка</div>
                <p className="mkt-page__hint" style={{ marginBottom: 12 }}>
                    Картинку рисует сам агент — image-модель включается в его настройках в Timeweb.
                    Мы отправляем отдельное сообщение с описанием обложки и сохраняем то, что вернулось.
                </p>
                <div className="mkt-actions" style={{ marginTop: 0, marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={Boolean(config.image_enabled)}
                            onChange={(e) => setField('image_enabled', e.target.checked)}
                        />
                        Запрашивать обложку у агента
                    </label>
                </div>
                <label className="mkt-field">
                    <span>
                        Запрос обложки — {'{title}'} подставится заголовком, {'{hint}'} описанием от агента
                    </span>
                    <textarea
                        rows={3}
                        value={config.image_prompt || ''}
                        onChange={(e) => setField('image_prompt', e.target.value)}
                        placeholder="Нарисуй горизонтальную обложку 16:9 для статьи «{title}». {hint} Без текста на изображении."
                    />
                </label>
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Инструмент и промокод в статье</div>
                <p className="mkt-page__hint" style={{ marginBottom: 12 }}>
                    Перед обращением к агенту выпускается промокод для новых пользователей и короткая
                    ссылка на регистрацию. Агент получает каталог инструментов и выбирает подходящий
                    либо отказывается. Если он отказался, выпущенное гасится. Статья с промокодом
                    всегда уходит в черновик, даже при включённой автопубликации.
                </p>
                <div className="mkt-actions" style={{ marginTop: 0, marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={Boolean(config.cta_enabled)}
                            onChange={(e) => setField('cta_enabled', e.target.checked)}
                        />
                        Подбирать инструмент и прикладывать промокод
                    </label>
                </div>
                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>Сумма на баланс, ₽</span>
                        <input
                            type="number"
                            min={0}
                            value={config.cta_promo_amount ?? 5000}
                            onChange={(e) => setField('cta_promo_amount', Number(e.target.value))}
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Срок жизни кода, дней (0 — бессрочно)</span>
                        <input
                            type="number"
                            min={0}
                            value={config.cta_promo_valid_days ?? 30}
                            onChange={(e) => setField('cta_promo_valid_days', Number(e.target.value))}
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Активаций на код</span>
                        <input
                            type="number"
                            min={1}
                            value={config.cta_promo_max_uses ?? 25}
                            onChange={(e) => setField('cta_promo_max_uses', Number(e.target.value))}
                        />
                    </label>
                    <label className="mkt-field">
                        <span>Статей с промокодом в неделю</span>
                        <input
                            type="number"
                            min={0}
                            value={config.cta_max_per_week ?? 2}
                            onChange={(e) => setField('cta_max_per_week', Number(e.target.value))}
                        />
                    </label>
                </div>
                <div className="mkt-card__title" style={{ marginTop: 18 }}>
                    Если инструмент не подобран
                </div>
                <p className="mkt-page__hint" style={{ marginBottom: 10 }}>
                    Агент подбирает инструмент не к каждой статье. Раньше в таких статьях
                    показывался общий блок «Free trial»; теперь — этот промокод. Поле пустое —
                    в статье не будет никакого блока.
                </p>
                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>Общий промокод (код существующего)</span>
                        <input
                            value={config.default_promo_code || ''}
                            onChange={(e) => setField('default_promo_code', e.target.value.toUpperCase())}
                            placeholder="BLOGSTART"
                        />
                    </label>
                    <label className="mkt-field" style={{ gridColumn: 'span 2' }}>
                        <span>Пояснение к нему</span>
                        <input
                            value={config.default_promo_note || ''}
                            onChange={(e) => setField('default_promo_note', e.target.value)}
                            placeholder="Промокод действует для новых аккаунтов при регистрации."
                        />
                    </label>
                </div>

                {Boolean(config.cta_enabled) && (
                    <div className="mkt-message">
                        Максимум обязательств в неделю:{' '}
                        {((config.cta_promo_amount ?? 0) *
                            (config.cta_promo_max_uses ?? 0) *
                            (config.cta_max_per_week ?? 0)).toLocaleString('ru-RU')}{' '}
                        ₽, если выберут все коды.
                    </div>
                )}
            </div>

            {telegram?.unavailable && (
                <div className="mkt-card">
                    <div className="mkt-card__title">Заметки в телеграм-канал</div>
                    <div className="mkt-message mkt-message--error">
                        Настройки недоступны: {telegram.error}. Раздел доступен только
                        суперадминистратору.
                    </div>
                </div>
            )}

            {telegram && !telegram.unavailable && (
                <div className="mkt-card">
                    <div className="mkt-card__title">Заметки в телеграм-канал</div>
                    <p className="mkt-page__hint" style={{ marginBottom: 12 }}>
                        Заметку пишет тот же агент по тексту статьи, публикуется она кнопкой из Blog CMS
                        после вашей вычитки. Комментарии читателей идут в группу обсуждений, привязанную
                        к каналу. Токен бота задаётся в окружении переменной TELEGRAM_CHANNEL_BOT_TOKEN:{' '}
                        {telegram.bot_token_set ? 'задан' : 'не задан'}.
                    </p>
                    <div className="mkt-grid">
                        <label className="mkt-field">
                            <span>Канал: @имя или числовой id</span>
                            <input
                                value={telegram.channel || ''}
                                onChange={(e) => setTelegram({ ...telegram, channel: e.target.value })}
                                placeholder="@anylex_news"
                            />
                        </label>
                    </div>
                    <label className="mkt-field" style={{ marginTop: 12 }}>
                        <span>Задача агенту на сокращение — пусто, возьмётся стандартная</span>
                        <textarea
                            rows={3}
                            value={telegram.note_task || ''}
                            onChange={(e) => setTelegram({ ...telegram, note_task: e.target.value })}
                            placeholder="Сократи статью до 700-900 знаков, первая строка — крючок."
                        />
                    </label>
                    <div className="mkt-actions">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input
                                type="checkbox"
                                checked={Boolean(telegram.enabled)}
                                onChange={(e) => setTelegram({ ...telegram, enabled: e.target.checked })}
                            />
                            Публикация включена
                        </label>
                        {telegram.enabled && !(telegram.channel || '').trim() && (
                            <span className="mkt-message mkt-message--error">
                                Укажите канал, иначе публикация вернёт ошибку
                            </span>
                        )}

                    </div>
                </div>
            )}

            <div className="mkt-actions">
                <button type="button" onClick={save} disabled={loading}>Сохранить</button>
                <select
                    value={manualTopic}
                    onChange={(e) => setManualTopic(e.target.value)}
                    style={{ padding: '8px 10px' }}
                >
                    <option value="">Общая задача, 1 статья</option>
                    {(config.topics || []).map((topic) => (
                        <option key={topic.name} value={topic.name}>
                            Тема «{topic.name}» — {topic.articles ?? 1} ст.
                        </option>
                    ))}
                </select>
                <button type="button" onClick={runNow} disabled={loading}>Запустить сейчас</button>
                {message && (
                    <span className={`mkt-message mkt-message--${message.type === 'error' ? 'error' : 'ok'}`}>
                        {message.text}
                    </span>
                )}
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">История прогонов</div>
                {runs.length === 0 ? (
                    <div className="mkt-empty">Прогонов ещё не было</div>
                ) : (
                    <table className="mkt-table">
                        <thead>
                            <tr>
                                <th>Начат</th>
                                <th>Запуск</th>
                                <th>Итог</th>
                                <th className="num">Статей</th>
                                <th className="num">Дубли</th>
                                <th className="num">Чужие источники</th>
                                <th className="num">Длительность</th>
                                <th className="num">Токены</th>
                                <th className="num">Стоимость</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((run) => (
                                <React.Fragment key={run.id}>
                                    <tr>
                                        <td>{run.started_at ? fmtDateTime(run.started_at) : '—'}</td>
                                        <td>{run.trigger === 'manual' ? 'вручную' : 'расписание'}</td>
                                        <td>
                                            <span
                                                className={`mkt-status mkt-status--${
                                                    run.status === 'success' ? 'on' : 'off'
                                                }`}
                                            >
                                                {STATUS_LABELS[run.status] || run.status}
                                            </span>
                                        </td>
                                        <td className="num">{run.articles_created}</td>
                                        <td className="num">{run.skipped_duplicates}</td>
                                        <td className="num">{run.rejected_sources}</td>
                                        <td className="num">{fmtDuration(run.duration_seconds)}</td>
                                        <td className="num">
                                            {((run.prompt_tokens || 0) + (run.completion_tokens || 0)).toLocaleString('ru-RU')}
                                        </td>
                                        <td className="num">
                                            {run.cost_rub != null ? `${run.cost_rub.toFixed(2)} ₽` : '—'}
                                        </td>
                                        <td>
                                            {(run.error || run.response_excerpt) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenRunId(openRunId === run.id ? null : run.id)}
                                                >
                                                    {openRunId === run.id ? 'Скрыть' : 'Подробнее'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {openRunId === run.id && (
                                        <tr>
                                            <td colSpan={8}>
                                                {run.error && (
                                                    <div className="mkt-message mkt-message--error">{run.error}</div>
                                                )}
                                                {run.response_excerpt && (
                                                    <pre className="mkt-code" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                                                        {run.response_excerpt}
                                                    </pre>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default AdminContentAgent;
